import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from '../contexts/LanguageContext';
import { useMarket, MARKETS } from '../contexts/MarketContext';
import { Menu, X, Phone, User, LogOut, Globe } from 'lucide-react';
import { Button } from './ui/button';
import axios from 'axios';

const LOGO_URL = "/logo.png";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FacebookIcon = () => (
  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
  </svg>
);
const InstagramIcon = () => (
  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
  </svg>
);
const TikTokIcon = () => (
  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
    <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.12V9a6.33 6.33 0 00-.79-.05A6.34 6.34 0 003.15 15.3a6.34 6.34 0 0010.86 4.43v-7.15a8.16 8.16 0 005.58 2.2V11.3a4.85 4.85 0 01-3.77-1.84V6.69h3.77z"/>
  </svg>
);
const LinkedInIcon = () => (
  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
  </svg>
);
const YouTubeIcon = () => (
  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
    <path d="M23.498 6.186a3.016 3.016 0 00-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 00.502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 002.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 002.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
  </svg>
);
const TwitterIcon = () => (
  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
  </svg>
);

const socialConfig = [
  { key: 'facebook_url', icon: FacebookIcon, label: 'Facebook', hoverColor: 'hover:text-[#1877F2]' },
  { key: 'instagram_url', icon: InstagramIcon, label: 'Instagram', hoverColor: 'hover:text-[#E4405F]' },
  { key: 'youtube_url', icon: YouTubeIcon, label: 'YouTube', hoverColor: 'hover:text-[#FF0000]' },
  { key: 'tiktok_url', icon: TikTokIcon, label: 'TikTok', hoverColor: 'hover:text-foreground' },
  { key: 'linkedin_url', icon: LinkedInIcon, label: 'LinkedIn', hoverColor: 'hover:text-[#0A66C2]' },
];

export default function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [langDropdownOpen, setLangDropdownOpen] = useState(false);
  const { user, isAuthenticated, logout } = useAuth();
  const { t, lang, switchLang, SUPPORTED_LANGS } = useTranslation();
  const { market, setMarket, marketLink } = useMarket();
  const location = useLocation();
  const [socials, setSocials] = useState({});

  useEffect(() => {
    axios.get(`${API}/site-settings`).then(r => {
      setSocials({
        facebook_url: r.data.facebook_url || '',
        instagram_url: r.data.instagram_url || '',
        youtube_url: r.data.youtube_url || '',
        tiktok_url: r.data.tiktok_url || '',
        linkedin_url: r.data.linkedin_url || '',
        custom_socials: r.data.custom_socials || [],
        company_phone: r.data.company_phone || '+359 890 300 600'
      });
    }).catch(() => {});
  }, []);

  const navLinks = [
    { href: marketLink('/'), label: t('nav.home') },
    { href: marketLink('/services'), label: t('nav.services') },
    { href: marketLink('/properties'), label: t('nav.projects') },
    { href: marketLink('/listings'), label: t('nav.listings') },
    { href: marketLink('/about'), label: t('nav.about') },
    { href: marketLink('/contact'), label: t('nav.contact') },
  ];

  const isActive = (path) => location.pathname === path;
  const activeSocials = socialConfig.filter(s => socials[s.key]);
  const currentLang = SUPPORTED_LANGS.find(l => l.code === lang);
  const currentMarket = MARKETS.find(m => m.code === market) || MARKETS[0];

  return (
    <header className="glass-header fixed top-0 left-0 right-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          <Link to="/" className="flex items-center" data-testid="header-logo">
            <img src={LOGO_URL} alt="GreenCastle" className="h-12 w-auto object-contain" />
          </Link>

          <nav className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                data-testid={`nav-${link.href.replace('/', '') || 'home'}`}
                className={`font-medium transition-colors ${
                  isActive(link.href) ? 'text-[#1B5E37]' : 'text-foreground/70 hover:text-[#1B5E37]'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          <div className="hidden md:flex items-center gap-3">
            {activeSocials.map((s) => (
              <a
                key={s.key}
                href={socials[s.key]}
                target="_blank"
                rel="noopener noreferrer"
                className={`text-muted-foreground ${s.hoverColor} transition-colors`}
                data-testid={`header-${s.key}`}
                title={s.label}
              >
                <s.icon />
              </a>
            ))}
            {(socials.custom_socials || []).filter(cs => cs.url).map((cs, idx) => (
              <a
                key={`custom-${idx}`}
                href={cs.url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-muted-foreground hover:text-[#1B5E37] transition-colors"
                title={cs.platform}
              >
                {cs.platform === 'twitter' ? <TwitterIcon /> : <span className="text-xs font-bold uppercase">{cs.platform.slice(0,2)}</span>}
              </a>
            ))}
            
            <a
              href={`tel:${(socials.company_phone || '+359890300600').replace(/\s/g, '')}`}
              className="flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-[#1B5E37] transition-colors ml-1"
              data-testid="header-phone"
            >
              <Phone className="w-4 h-4" />
              {socials.company_phone || '+359 890 300 600'}
            </a>

            {/* Market Switcher */}
            <div className="relative ml-1" data-testid="market-switcher">
              <button
                onClick={() => setLangDropdownOpen(!langDropdownOpen)}
                className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-border text-sm font-medium text-foreground/70 hover:text-[#1B5E37] hover:border-[#1B5E37]/30 transition-colors"
                data-testid="market-switcher-btn"
              >
                <span className="text-base leading-none">{currentMarket?.flag}</span>
                <span>{currentMarket?.code?.toUpperCase()}</span>
              </button>
              {langDropdownOpen && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setLangDropdownOpen(false)} />
                  <div className="absolute right-0 top-full mt-1 bg-white border border-border rounded-lg shadow-lg z-50 min-w-[160px] py-1">
                    {MARKETS.map((m) => (
                      <button
                        key={m.code}
                        onClick={() => {
                          localStorage.setItem('gc_market_choice', m.code);
                          // Cookie mirror so the Vercel edge middleware can also see the choice
                          document.cookie = `gc_market_choice=${m.code}; path=/; max-age=31536000; SameSite=Lax`;
                          setLangDropdownOpen(false);
                          window.location.href = m.path;
                        }}
                        className={`w-full text-left px-4 py-2 text-sm transition-colors flex items-center gap-2 ${
                          market === m.code ? 'bg-[#1B5E37]/10 text-[#1B5E37] font-semibold' : 'text-foreground hover:bg-secondary'
                        }`}
                        data-testid={`market-option-${m.code}`}
                      >
                        <span className="text-base leading-none">{m.flag}</span>
                        <span>{m.native}</span>
                        {market === m.code && <span className="ml-auto text-[10px] text-[#1B5E37]">✓</span>}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
            
            {isAuthenticated ? (
              <div className="flex items-center gap-3">
                <Link to="/admin">
                  <Button variant="outline" size="sm" data-testid="admin-panel-btn">
                    <User className="w-4 h-4 mr-2" />
                    {user?.name}
                  </Button>
                </Link>
                <Button variant="ghost" size="sm" onClick={logout} data-testid="logout-btn">
                  <LogOut className="w-4 h-4" />
                </Button>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <Link to="/login">
                  <Button variant="default" size="sm" className="bg-[#1B5E37] hover:bg-[#154a2c]" data-testid="login-btn">
                    {t('nav.login')}
                  </Button>
                </Link>
                <Button variant="outline" size="sm" data-testid="empty-btn">
                  &nbsp;
                </Button>
              </div>
            )}
          </div>

          <button
            className="md:hidden p-2"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-btn"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {mobileMenuOpen && (
        <div className="md:hidden bg-white border-t border-border">
          <nav className="flex flex-col p-4 gap-2">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                onClick={() => setMobileMenuOpen(false)}
                className={`py-3 px-4 rounded-lg font-medium ${
                  isActive(link.href) ? 'bg-[#1B5E37] text-white' : 'text-foreground hover:bg-secondary'
                }`}
              >
                {link.label}
              </Link>
            ))}
            <hr className="my-2" />
            {/* Market switcher mobile */}
            <div className="flex items-center gap-2 px-4 py-2 flex-wrap" data-testid="mobile-market-switcher">
              {MARKETS.map((m) => (
                <button
                  key={m.code}
                  onClick={() => {
                    localStorage.setItem('gc_market_choice', m.code);
                    // Cookie mirror so the Vercel edge middleware can also see the choice
                    document.cookie = `gc_market_choice=${m.code}; path=/; max-age=31536000; SameSite=Lax`;
                    window.location.href = m.path;
                  }}
                  className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors flex items-center gap-1 ${
                    market === m.code ? 'bg-[#1B5E37] text-white' : 'text-foreground/70 hover:bg-secondary'
                  }`}
                  data-testid={`mobile-market-${m.code}`}
                >
                  <span>{m.flag}</span> {m.code.toUpperCase()}
                </button>
              ))}
            </div>
            <hr className="my-2" />
            <div className="flex items-center gap-4 px-4 py-2">
              {activeSocials.map((s) => (
                <a key={s.key} href={socials[s.key]} target="_blank" rel="noopener noreferrer" className={`text-muted-foreground ${s.hoverColor} transition-colors`}>
                  <s.icon />
                </a>
              ))}
            </div>
            {isAuthenticated ? (
              <>
                <Link to="/admin" onClick={() => setMobileMenuOpen(false)} className="py-3 px-4 rounded-lg font-medium text-foreground hover:bg-secondary flex items-center gap-2">
                  <User className="w-4 h-4" /> {t('nav.admin')}
                </Link>
                <button onClick={() => { logout(); setMobileMenuOpen(false); }} className="py-3 px-4 rounded-lg font-medium text-foreground hover:bg-secondary flex items-center gap-2 w-full text-left">
                  <LogOut className="w-4 h-4" /> {t('nav.logout')}
                </button>
              </>
            ) : (
              <Link to="/login" onClick={() => setMobileMenuOpen(false)} className="py-3 px-4 rounded-lg font-medium bg-[#1B5E37] text-white text-center">
                {t('nav.login')}
              </Link>
            )}
          </nav>
        </div>
      )}
    </header>
  );
}
